/**
 * usePaymentState - Composable pour gérer l'état du paiement
 *
 * Ce composable gère les différents états d'un paiement:
 * - idle: aucun paiement en cours
 * - processing: paiement en cours de traitement
 * - succeeded: paiement réussi
 * - failed: paiement échoué
 * - cancelled: paiement annulé par l'utilisateur
 */

import { ref, computed } from 'vue';

export function usePaymentState() {
  // État du paiement
  const paymentState = ref('idle'); // 'idle' | 'processing' | 'succeeded' | 'failed' | 'cancelled'
  const paymentError = ref(null);
  const paymentData = ref(null);

  // Computed properties pour faciliter l'utilisation
  const isIdle = computed(() => paymentState.value === 'idle');
  const isProcessing = computed(() => paymentState.value === 'processing');
  const isSucceeded = computed(() => paymentState.value === 'succeeded');
  const isFailed = computed(() => paymentState.value === 'failed');
  const isCancelled = computed(() => paymentState.value === 'cancelled');
  const isFinished = computed(() =>
    paymentState.value === 'succeeded' ||
    paymentState.value === 'failed' ||
    paymentState.value === 'cancelled'
  );

  /**
   * Définit l'état à "processing"
   * @param {object} data - Données du paiement optionnelles
   */
  function setProcessing(data = null) {
    paymentState.value = 'processing';
    paymentData.value = data;
    paymentError.value = null;
  }

  /**
   * Définit l'état à "succeeded"
   * @param {object} data - Données du paiement réussi
   */
  function setSucceeded(data) {
    paymentState.value = 'succeeded';
    paymentData.value = data;
    paymentError.value = null;
  }

  /**
   * Définit l'état à "failed"
   * @param {Error|string} error - Erreur survenue
   */
  function setFailed(error) {
    paymentState.value = 'failed';
    paymentError.value = error instanceof Error ? error.message : error;
  }

  /**
   * Définit l'état à "cancelled"
   * @param {object} data - Données d'annulation optionnelles
   */
  function setCancelled(data = null) {
    paymentState.value = 'cancelled';
    paymentData.value = data;
    paymentError.value = null;
  }

  /**
   * Réinitialise l'état à "idle"
   */
  function reset() {
    paymentState.value = 'idle';
    paymentData.value = null;
    paymentError.value = null;
  }

  return {
    // État
    paymentState: computed(() => paymentState.value),
    paymentError: computed(() => paymentError.value),
    paymentData: computed(() => paymentData.value),

    // Computed properties
    isIdle,
    isProcessing,
    isSucceeded,
    isFailed,
    isCancelled,
    isFinished,

    // Actions
    setProcessing,
    setSucceeded,
    setFailed,
    setCancelled,
    reset
  };
}
